import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const PHONE_COUNTRIES = [
  { iso: 'US', flag: '🇺🇸', name: 'United States', dialCode: '+1' },
  { iso: 'CA', flag: '🇨🇦', name: 'Canada', dialCode: '+1' },
  { iso: 'MX', flag: '🇲🇽', name: 'Mexico', dialCode: '+52' },
  { iso: 'BR', flag: '🇧🇷', name: 'Brazil', dialCode: '+55' },
  { iso: 'AR', flag: '🇦🇷', name: 'Argentina', dialCode: '+54' },
  { iso: 'CL', flag: '🇨🇱', name: 'Chile', dialCode: '+56' },
  { iso: 'CO', flag: '🇨🇴', name: 'Colombia', dialCode: '+57' },
  { iso: 'PE', flag: '🇵🇪', name: 'Peru', dialCode: '+51' },
  { iso: 'EC', flag: '🇪🇨', name: 'Ecuador', dialCode: '+593' },
  { iso: 'UY', flag: '🇺🇾', name: 'Uruguay', dialCode: '+598' },
  { iso: 'PY', flag: '🇵🇾', name: 'Paraguay', dialCode: '+595' },
  { iso: 'BO', flag: '🇧🇴', name: 'Bolivia', dialCode: '+591' },
  { iso: 'PA', flag: '🇵🇦', name: 'Panama', dialCode: '+507' },
  { iso: 'CR', flag: '🇨🇷', name: 'Costa Rica', dialCode: '+506' },
  { iso: 'DO', flag: '🇩🇴', name: 'Dominican Republic', dialCode: '+1' },
  { iso: 'ES', flag: '🇪🇸', name: 'Spain', dialCode: '+34' },
  { iso: 'PT', flag: '🇵🇹', name: 'Portugal', dialCode: '+351' },
  { iso: 'GB', flag: '🇬🇧', name: 'United Kingdom', dialCode: '+44' },
  { iso: 'FR', flag: '🇫🇷', name: 'France', dialCode: '+33' },
  { iso: 'DE', flag: '🇩🇪', name: 'Germany', dialCode: '+49' },
  { iso: 'IT', flag: '🇮🇹', name: 'Italy', dialCode: '+39' },
  { iso: 'NL', flag: '🇳🇱', name: 'Netherlands', dialCode: '+31' },
  { iso: 'BE', flag: '🇧🇪', name: 'Belgium', dialCode: '+32' },
  { iso: 'CH', flag: '🇨🇭', name: 'Switzerland', dialCode: '+41' },
  { iso: 'IE', flag: '🇮🇪', name: 'Ireland', dialCode: '+353' },
  { iso: 'SE', flag: '🇸🇪', name: 'Sweden', dialCode: '+46' },
  { iso: 'NO', flag: '🇳🇴', name: 'Norway', dialCode: '+47' },
  { iso: 'DK', flag: '🇩🇰', name: 'Denmark', dialCode: '+45' },
  { iso: 'AU', flag: '🇦🇺', name: 'Australia', dialCode: '+61' },
  { iso: 'NZ', flag: '🇳🇿', name: 'New Zealand', dialCode: '+64' },
  { iso: 'IN', flag: '🇮🇳', name: 'India', dialCode: '+91' },
  { iso: 'JP', flag: '🇯🇵', name: 'Japan', dialCode: '+81' },
  { iso: 'SG', flag: '🇸🇬', name: 'Singapore', dialCode: '+65' },
  { iso: 'AE', flag: '🇦🇪', name: 'United Arab Emirates', dialCode: '+971' }
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TURNSTILE_SITE_KEY = '0x4AAAAAAEE7L9rnccTCrK4H';
const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || '/contact.php';

const COUNTRY_OPTIONS = PHONE_COUNTRIES.map(({ iso, flag, name }) => ({
  iso,
  flag,
  name
}));

const initialFormState = {
  companyName: '',
  country: '',
  website: '',
  firstName: '',
  lastName: '',
  phoneCountry: 'US',
  phoneNumber: '',
  email: ''
};

const normalizeWebsite = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const getFlagUrl = (isoCode) => `https://flagcdn.com/24x18/${isoCode.toLowerCase()}.png`;

const getSubmissionErrorMessage = (message, t) => {
  if (!message) {
    return t('common.form.error');
  }

  if (message.includes('Missing Turnstile secret')) {
    return t('common.form.server_configuration_error');
  }

  return message;
};

const SearchableSelector = ({
  options,
  selectedOption,
  onSelect,
  label,
  searchPlaceholder,
  renderTrigger,
  renderOption
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;

    return options.filter((option) =>
      `${option.name} ${option.dialCode} ${option.iso}`.toLowerCase().includes(normalizedQuery)
    );
  }, [options, query]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-[#141414] focus:outline-none focus:ring-2 focus:ring-[#141414]/20 focus:border-[#141414] flex items-center justify-between gap-2"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
      >
        {renderTrigger(selectedOption)}
        <span className="text-xs text-gray-500">▼</span>
      </button>

      {isOpen ? (
        <div className="absolute top-[calc(100%+8px)] left-0 z-30 w-72 rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
          <div className="p-3 border-b border-gray-100">
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm text-[#141414] focus:outline-none focus:ring-2 focus:ring-[#141414]/20 focus:border-[#141414]"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-2">
            {filteredOptions.map((option) => (
              <button
                key={`${option.iso}-${option.dialCode}`}
                type="button"
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                  setQuery('');
                }}
                className="w-full px-3 py-2.5 text-left hover:bg-gray-50 transition flex items-center justify-between gap-3"
              >
                {renderOption(option)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ContactForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialFormState);
  const [status, setStatus] = useState('idle');
  const [feedback, setFeedback] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const widgetContainerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const selectedPhoneCountry = useMemo(
    () => PHONE_COUNTRIES.find((country) => country.iso === form.phoneCountry) || PHONE_COUNTRIES[0],
    [form.phoneCountry]
  );
  const selectedCountryOption = useMemo(
    () => COUNTRY_OPTIONS.find((country) => country.name === form.country) || null,
    [form.country]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value
    }));
  };

  const resetTurnstile = () => {
    setTurnstileToken('');
    if (window.turnstile && widgetIdRef.current !== null) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  useEffect(() => {
    const renderWidget = () => {
      if (!window.turnstile || !widgetContainerRef.current || widgetIdRef.current !== null) return;

      widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        size: 'flexible',
        action: 'turnstile-spin-v2',
        callback: (token) => {
          setTurnstileToken(token);
          setFeedback('');
        },
        'expired-callback': () => {
          setTurnstileToken('');
        },
        'error-callback': () => {
          setTurnstileToken('');
        }
      });
    };

    if (window.turnstile) {
      renderWidget();
      return undefined;
    }

    const existingScript = document.querySelector('script[data-turnstile-script="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', renderWidget);
      return () => existingScript.removeEventListener('load', renderWidget);
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = 'true';
    script.addEventListener('load', renderWidget);
    document.body.appendChild(script);

    return () => script.removeEventListener('load', renderWidget);
  }, []);

  useEffect(() => {
    return () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const validateForm = () => {
    if (
      !form.companyName.trim() ||
      !form.country.trim() ||
      !form.website.trim() ||
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.phoneNumber.trim() ||
      !form.email.trim()
    ) {
      setFeedback(t('common.form.required_fields'));
      return null;
    }

    if (!EMAIL_REGEX.test(form.email.trim())) {
      setFeedback(t('common.form.invalid_email'));
      return null;
    }

    if (!turnstileToken) {
      setFeedback(t('common.form.captcha_required'));
      return null;
    }

    const normalizedWebsite = normalizeWebsite(form.website);

    try {
      const url = new URL(normalizedWebsite);
      if (!url.hostname) {
        throw new Error('Missing hostname');
      }
    } catch (error) {
      setFeedback(t('common.form.invalid_website'));
      return null;
    }

    return normalizedWebsite;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback('');

    const normalizedWebsite = validateForm();
    if (!normalizedWebsite) return;

    setStatus('submitting');

    try {
      const payload = new FormData();
      payload.append('company_name', form.companyName.trim());
      payload.append('country', form.country.trim());
      payload.append('website', normalizedWebsite);
      payload.append('first_name', form.firstName.trim());
      payload.append('last_name', form.lastName.trim());
      payload.append('phone', `${selectedPhoneCountry.dialCode} ${form.phoneNumber.trim()}`);
      payload.append('email', form.email.trim());
      payload.append('source_page', window.location.href);
      payload.append('cf-turnstile-response', turnstileToken);

      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: payload
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.success === false || result.success === 'false') {
        throw new Error(getSubmissionErrorMessage(result.message, t));
      }

      setStatus('success');
      setFeedback(t('common.form.success'));
      setForm(initialFormState);
      resetTurnstile();
    } catch (error) {
      setStatus('error');
      setFeedback(error.message || t('common.form.error'));
      resetTurnstile();
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="companyName" className="block text-sm font-semibold text-[#141414] mb-2">
            {t('common.form.company_name')}
          </label>
          <input
            id="companyName"
            name="companyName"
            type="text"
            value={form.companyName}
            onChange={handleChange}
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-[#141414] focus:outline-none focus:ring-2 focus:ring-[#141414]/20 focus:border-[#141414]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#141414] mb-2">
            {t('common.form.country')}
          </label>
          <SearchableSelector
            options={COUNTRY_OPTIONS}
            selectedOption={selectedCountryOption}
            onSelect={(option) =>
              setForm((current) => ({
                ...current,
                country: option.name,
                phoneCountry: option.iso
              }))
            }
            label={t('common.form.country')}
            searchPlaceholder={t('common.form.country_search_placeholder')}
            renderTrigger={(option) => (
              <span className="flex items-center gap-2 min-w-0">
                {option ? (
                  <img
                    src={getFlagUrl(option.iso)}
                    alt={option.name}
                    className="w-5 h-4 rounded-[2px] object-cover flex-shrink-0"
                  />
                ) : (
                  <span className="text-base leading-none">🌐</span>
                )}
                <span className={`truncate ${option ? 'font-semibold text-[#141414]' : 'text-gray-500'}`}>
                  {option?.name || t('common.form.country_search_placeholder')}
                </span>
              </span>
            )}
            renderOption={(option) => (
              <span className="flex items-center gap-3 min-w-0">
                <img
                  src={getFlagUrl(option.iso)}
                  alt={option.name}
                  className="w-5 h-4 rounded-[2px] object-cover flex-shrink-0"
                />
                <span className="text-sm text-[#141414] truncate">{option.name}</span>
              </span>
            )}
          />
        </div>

        <div>
          <label htmlFor="website" className="block text-sm font-semibold text-[#141414] mb-2">
            {t('common.form.website')}
          </label>
          <input
            id="website"
            name="website"
            type="text"
            value={form.website}
            onChange={handleChange}
            placeholder="https://example.com"
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-[#141414] focus:outline-none focus:ring-2 focus:ring-[#141414]/20 focus:border-[#141414]"
            required
          />
        </div>

        <div>
          <label htmlFor="firstName" className="block text-sm font-semibold text-[#141414] mb-2">
            {t('common.form.first_name')}
          </label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            value={form.firstName}
            onChange={handleChange}
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-[#141414] focus:outline-none focus:ring-2 focus:ring-[#141414]/20 focus:border-[#141414]"
            required
          />
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-semibold text-[#141414] mb-2">
            {t('common.form.last_name')}
          </label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            value={form.lastName}
            onChange={handleChange}
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-[#141414] focus:outline-none focus:ring-2 focus:ring-[#141414]/20 focus:border-[#141414]"
            required
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-[#141414] mb-2">
            {t('common.form.email')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-[#141414] focus:outline-none focus:ring-2 focus:ring-[#141414]/20 focus:border-[#141414]"
            inputMode="email"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="phoneNumber" className="block text-sm font-semibold text-[#141414] mb-2">
            {t('common.form.phone_number')}
          </label>
          <div className="grid grid-cols-[112px_minmax(0,1fr)] sm:grid-cols-[124px_minmax(0,1fr)] gap-3">
            <SearchableSelector
              options={PHONE_COUNTRIES}
              selectedOption={selectedPhoneCountry}
              onSelect={(option) =>
                setForm((current) => ({
                  ...current,
                  phoneCountry: option.iso
                }))
              }
              label={t('common.form.phone_code')}
              searchPlaceholder={t('common.form.phone_search_placeholder')}
              renderTrigger={(option) => (
                <span className="flex items-center gap-2 min-w-0">
                  <img
                    src={getFlagUrl(option.iso)}
                    alt={option.name}
                    className="w-5 h-4 rounded-[2px] object-cover flex-shrink-0"
                  />
                  <span className="font-semibold whitespace-nowrap">{option.dialCode}</span>
                </span>
              )}
              renderOption={(option) => (
                <>
                  <span className="flex items-center gap-3 min-w-0">
                    <img
                      src={getFlagUrl(option.iso)}
                      alt={option.name}
                      className="w-5 h-4 rounded-[2px] object-cover flex-shrink-0"
                    />
                    <span className="text-sm text-[#141414] truncate">{option.name}</span>
                  </span>
                  <span className="text-sm font-semibold text-gray-600">{option.dialCode}</span>
                </>
              )}
            />

            <input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              value={form.phoneNumber}
              onChange={handleChange}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-[#141414] focus:outline-none focus:ring-2 focus:ring-[#141414]/20 focus:border-[#141414]"
              inputMode="tel"
              required
            />
          </div>
          <p className="mt-2 text-xs text-gray-500">{t('common.form.phone_help')}</p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-[#141414] mb-2">
            {t('common.form.captcha_label')}
          </label>
          <div className="min-h-[92px] rounded-xl border border-gray-300 bg-white px-3 py-3 flex items-center justify-center overflow-hidden">
            <div ref={widgetContainerRef} className="w-full max-w-full" />
          </div>
        </div>

        {feedback ? (
          <div
            className={`md:col-span-2 rounded-xl px-4 py-3 text-sm ${
              status === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {feedback}
          </div>
        ) : null}

        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full rounded-xl bg-[#141414] text-white py-3.5 px-6 text-sm font-semibold hover:bg-gray-900 transition disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? t('common.form.submitting') : t('common.form.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
